import { ApiService } from "@/services/api/ApiService";
import { getStaticArticleBySlug, getStaticArticles } from "@/lib/learn/articleRegistry";
import { LearnArticle, LearnArticleFilters, LearnProgressRow } from "@/types/learn";

/**
 * Front-end Learn service.
 *
 * Reads prefer the DB copy (which carries per-user progress) and fall back to
 * the static `LEARN_ARTICLE_REGISTRY` if the DB hasn't been seeded in the
 * current environment yet. Writes always go through the API.
 */
export class LearnService {
  static async list(
    filters?: LearnArticleFilters,
    limit: number = 100,
    offset: number = 0,
  ): Promise<{ articles: LearnArticle[]; count: number }> {
    try {
      const response = await ApiService.invoke<{ articles: LearnArticle[]; count: number }>({
        resource: "learn",
        action: "all",
        data: { filters, limit, offset },
      });
      const articles = response?.articles ?? [];
      if (articles.length > 0) {
        return { articles, count: response?.count ?? articles.length };
      }
    } catch (error) {
      console.warn("[LearnService] list API call failed, using static registry:", error);
    }
    const fallback = getStaticArticles(filters);
    return { articles: fallback, count: fallback.length };
  }

  static async getBySlug(slug: string): Promise<LearnArticle | null> {
    try {
      const response = await ApiService.invoke<{ article: LearnArticle }>({
        resource: "learn",
        action: "get",
        data: { slug },
      });
      if (response?.article) return response.article;
    } catch (error) {
      console.warn("[LearnService] getBySlug API call failed, using static registry:", error);
    }
    return getStaticArticleBySlug(slug);
  }

  static async getProgress(): Promise<LearnProgressRow[]> {
    try {
      const response = await ApiService.invoke<{ progress: LearnProgressRow[] }>({
        resource: "learn",
        action: "progress",
      });
      return response?.progress ?? [];
    } catch (error) {
      console.warn("[LearnService] getProgress error:", error);
      return [];
    }
  }

  static async markComplete(articleId: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke<{ success: boolean }>({
        resource: "learn",
        action: "mark-complete",
        id: articleId,
      });
      return response?.success ?? false;
    } catch (error) {
      console.warn("[LearnService] markComplete error:", error);
      return false;
    }
  }

  static async markIncomplete(articleId: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke<{ success: boolean }>({
        resource: "learn",
        action: "mark-incomplete",
        id: articleId,
      });
      return response?.success ?? false;
    } catch (error) {
      console.warn("[LearnService] markIncomplete error:", error);
      return false;
    }
  }
}
